<template>
  <div>
    <div v-if="adjustmentCount > 0" class="f-s-16 p-l-15 col-md-offset-3 col-sm-offset-2">Adjust {{ adjustmentCount }} call(s)</div>
    <ValidationObserver
      v-if="!isOnConfirmation"
      ref="formValidation"
      v-slot="{ invalid }"
      slim
    >
      <form
        ref="container"
        role="form"
        class="form-horizontal p-t-10 p-b-30"
        novalidate
        @submit.prevent
      >
        <div
          class="form-group"
        >
          <label class="col-sm-2 col-md-3 control-label">{{ $t(conversionAdjustmentLabel) }}
            <a
              v-tooltip.right="$t(conversionAdjustmentTooltip)"
            >
              &nbsp;&nbsp;<i class="fa fa-question-circle" />
            </a>
          </label>

          <ValidationProvider
            v-slot="{ classes, failedRules }"
            :rules="conversionAmountValidationRule"
            slim
          >
            <div class="col-xs-3 col-sm-2 col-md-1">
              <span class="currency">$</span>

              <input
                v-model.number="data.conversionAdjustmentAmount"
                v-focus
                type="number"
                class="form-control"
                :min="0"
                :class="classes"
              >
            </div>

            <div
              v-if="failedRules.min_value"
              v-t="'CONVERSION_ADJUSTMENT_MIN_INVALID'" 
              class="col-md-9 col-xs-12 col-lg-9 col-lg-offset-3 col-md-offset-3 col-sm-offset-3 text-danger hidden-xs"
            />
          </ValidationProvider>
        </div>

        <div
          v-if="isAdmin"
          class="form-group"
        >
          <label class="col-sm-2 col-md-3 control-label">{{ $t('PAYOUT_ADJUSTMENT_AMOUNT_LABEL') }}
            <a
              v-tooltip.right="$t('PAYOUT_ADJUSTMENT_AMOUNT_TT')"
            >
              &nbsp;&nbsp;<i class="fa fa-question-circle" />
            </a>
          </label>

          <ValidationProvider
            v-slot="{ classes, failedRules }"
            :rules="payoutAmountValidationRule"
            slim
          >
            <div class="col-xs-3 col-sm-2 col-md-1">
              <span class="currency">$</span>
          
              <input
                v-model.number="data.payoutAdjustmentAmount"
                type="number"
                class="form-control"
                :min="-callPayoutAmount"
                :class="classes"
              >
            </div>

            <div
              v-if="failedRules.min_value"
              v-t="'PAYOUT_ADJUSTMENT_MIN_INVALID'" 
              class="col-md-9 col-xs-12 col-lg-9 col-lg-offset-3 col-md-offset-3 col-sm-offset-3 text-danger hidden-xs"
            />
          </ValidationProvider>
        </div>

        <div
          v-if="!isAdmin"
          class="form-group"
        >
          <label class="col-sm-2 col-md-3 control-label">{{ $t('CONVERSION_ADJUSTMENT_REASON_LABEL') }}
            <a
              v-tooltip.right="$t('CONVERSION_ADJUSTMENT_REASON_TT')"
            >
              &nbsp;&nbsp;<i class="fa fa-question-circle" />
            </a>
          </label>

          <div class="col-xs-12 col-sm-5 col-md-3">
            <input
              v-model="data.reason"
              class="form-control"
            >
          </div>
        </div>

        <div class="form-group">
          <div class="col-sm-offset-2 col-md-offset-3 col-md-7">
            <PrimaryButton
              size="sm"
              :disabled="invalid || emptyValues"
              :show-loader-on-form="showLoader"
              @click="isOnConfirmation = true"
            >
              <span
                v-if="isAdmin"
                v-t="'PAYOUT_ADJUSTMENT_SEND_BTN'"
              />
              <span
                v-if="!isAdmin"
                v-t="'CONVERSION_ADJUSTMENT_SEND_BTN'"
              />
            </PrimaryButton>

            <button
              v-t="'CANCEL'"
              type="button"
              class="btn btn-sm btn-danger m-l-15"
              @click="closeForm"
            />
          </div>
        </div>
      </form>
    </ValidationObserver>

    <div
      v-if="isOnConfirmation"
      ref="container"
      class="ag-grid__overlay-form-box"
    >
      <CallLogActionConfirmation
        :return-to-review-label="$t('REVIEW_CHANGES')"
        :pending-bulk-call-adjustment-by-logs="pendingBulkCallAdjustmentByLogs"
        @confirmAction="callPaymentAdjustmentSubmit"
        @cancelForm="closeForm"
        @returnToReviewDetails="isOnConfirmation = false"
      >
        <p
          v-if="!selectedCallsCount"
          class="p-l-15 col-sm-offset-3 col-md-5 col-sm-6 float-none"
        >
          {{ 
            isBulkAdjustment 
              ? 
                $t('BULK_CONVERSION_ADJUSTMENT_MESSAGE', { adjustmentCount }) 
              : 
                $t('SINGLE_CONVERSION_ADJUSTMENT_MESSAGE')
          }}
        </p>
        <div class="form-group">
          <label class="col-sm-3 control-label">{{ $t(conversionAdjustmentLabel) }}</label>

          <div class="col-sm-4 col-md-3">
            <label class="form-control-static">
              <template v-if="conversionAdjustmentAmountHasValue">
                {{ data.conversionAdjustmentAmount | currency }}
              </template>
              <span
                v-else
                class="text-muted"
              >
                {{ $t('ORIGINAL_VALUE_UNCHANGED') }}
              </span>
            </label>
          </div>
        </div>

        <div
          v-if="isAdmin"
          class="form-group"
        >
          <label class="col-sm-3 control-label">{{ $t('PAYOUT_ADJUSTMENT_AMOUNT_LABEL') }}</label>

          <div class="col-sm-4 col-md-3">
            <label class="form-control-static">
              <template v-if="payoutAdjustmentAmountHasValue">
                {{ data.payoutAdjustmentAmount | currency }}
              </template>
              <span
                v-else
                class="text-muted"
              >
                {{ $t('ORIGINAL_VALUE_UNCHANGED') }}
              </span>
            </label>
          </div>
        </div>
      </CallLogActionConfirmation>
    </div>
  </div>
</template>

<script>
import { isEmpty, isNumber } from 'lodash';
import callLogActionFormsMixin from '../callLogActionFormsMixin';
import CallLogActionConfirmation from '../CallLogActionConfirmation';
import PrimaryButton from '@/core/components/PrimaryButton';
import sharedMessages from '@/core/locales';
import messages from './locales';
import { currencyFormatter } from '../../../AgGridUtils';

export default {
  name: 'CallPaymentOverrideForm',
  components: {
    PrimaryButton,
    CallLogActionConfirmation
  },
  filters: {
    currency(value) {
      return currencyFormatter.format(value);
    }
  },
  mixins: [callLogActionFormsMixin],
  props: {
    isBulkAdjustment: {
      type: Boolean,
      default: false
    },
    isAdmin: {
      type: Boolean,
      default: false
    },
    selectedCallsCount: {
      type: Number,
      default: 0
    },
    totalCallsCount: {
      type: Number,
      default: 0
    },
    call: {
      type: Object,
      default() {
        return {};
      }
    },
    cachedData: {
      type: Object,
      default() {
        return {};
      }
    },
    pendingBulkCallAdjustmentByLogs: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      data: {
        conversionAdjustmentAmount: '',
        payoutAdjustmentAmount: '',
        reason: ''
      },
      callConversionAmount: '',
      callPayoutAmount: '',
      isOnConfirmation: false,
    };
  },

  computed: {
    conversionAmountValidationRule() {
      return `min_value:0`;
    },
    payoutAmountValidationRule() {
      return `min_value:0`;
    },
    conversionAdjustmentLabel() {
      return !this.isAdmin ? 'PAYOUT_ADJUSTMENT_AMOUNT_LABEL' : 'CONVERSION_ADJUSTMENT_AMOUNT_LABEL';
    },
    conversionAdjustmentTooltip() {
      return !this.isAdmin ? 'PAYOUT_ADJUSTMENT_AMOUNT_TT' : 'CONVERSION_ADJUSTMENT_AMOUNT_TT';
    },
    AmountsHaveChanged() {
      return this.data.conversionAdjustmentAmount != this.callConversionAmount || this.data.payoutAdjustmentAmount != this.callPayoutAmount;
    },
    adjustmentCount() {
      return this.selectedCallsCount ? this.selectedCallsCount : this.totalCallsCount;
    },
    conversionAdjustmentAmountHasValue() {
      return isNumber(this.data.conversionAdjustmentAmount);
    },
    payoutAdjustmentAmountHasValue() {
      return isNumber(this.data.payoutAdjustmentAmount);
    },
    emptyValues() {
      return !this.conversionAdjustmentAmountHasValue && !this.payoutAdjustmentAmountHasValue;
    },
    dataToBeSaved() {
      return {
        ...this.data,
        adjustConversion: this.conversionAdjustmentAmountHasValue,
        adjustPayout: this.payoutAdjustmentAmountHasValue
      };
    }
  },

  mounted () {
    this.isContainerHeightChangeable = true;
    this.setDefaultAdjustmentAmounts();
    
    if (!isEmpty(this.cachedData)) {
      this.data = {
        ...this.cachedData
      };
    }
  },

  beforeDestroy () {
    this.$emit('cacheFormData', this.data);
  },

  methods: {
    setDefaultAdjustmentAmounts() {
      const { conversionAmount, payoutAmount } = this.getPayoutConversionFromCall(this.call);

      this.callConversionAmount = conversionAmount;
      this.callPayoutAmount = payoutAmount;

      this.data.conversionAdjustmentAmount = this.callConversionAmount;
      this.data.payoutAdjustmentAmount = this.callPayoutAmount;
    },
    callPaymentAdjustmentSubmit() {
      this.$emit('callPaymentAdjustmentSubmit', this.dataToBeSaved);
    },
    getPayoutConversionFromCall(call) {
      const conversionAmount = this.isAdmin ? call.conversionAmount : call.buyerConversionAmount;

      return {
        conversionAmount: conversionAmount,
        payoutAmount: call.payoutAmount
      };
    },
    
  },
  i18n: {
    locale: 'en',
    messages,
    sharedMessages
  }
};
</script>

<style scoped>
.ag-grid__overlay-form-box {
  top: 0px;
}
</style>